import { changeSentryUser } from '../sentry';
import { getTokenPayload, storageRequireLogin, userSubject } from './authService';

/**
 * Global fetch interceptor that automatically updates the JWT token when the backend
 * sends a refreshed one in the X-Auth-Token header.
 *
 * The backend extend middleware sends a new token when the current token is about
 * to expire (< 2 days remaining) OR when the token is older than 1 day.
 *
 * This interceptor catches fetch calls made via window.fetch:
 * - Use case definition fetches (manual refresh button)
 * - Amount in EAN config fetches
 * - Any other direct API calls
 *
 * Note: PouchDB uses its own fetch handler (PouchDB.fetch) which bypasses this interceptor.
 * This is intentional - we don't want to process token refresh on every sync request.
 * Token refresh happens on occasional API calls instead.
 */
export function setupFetchInterceptor() {
  const originalFetch = window.fetch;

  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    // Call the original fetch
    const response = await originalFetch(input, init);

    // Check if backend sent a refreshed token
    const newToken = response.headers.get('x-auth-token');
    if (newToken) {
      const tokenPayload = getTokenPayload(newToken);

      // Update token in localStorage
      localStorage.setItem('jwtToken', newToken);
      localStorage.setItem('tenantInfo', JSON.stringify(tokenPayload));

      // Update userSubject to notify all subscribers (e.g., currency display)
      userSubject.next(tokenPayload);

      // Update Sentry user context
      changeSentryUser(tokenPayload);

      // Update requireLogin in localStorage to match current token
      if (tokenPayload?.requireLogin !== undefined) {
        localStorage.setItem(storageRequireLogin, String(tokenPayload.requireLogin));
      }
    }

    return response;
  };
}
